import React, {
  forwardRef,
  Ref,
  useState,
  useImperativeHandle,
  useEffect,
  useRef,
  ReactNode,
  JSX,
  RefObject
} from 'react';
import { observer, useLocalObservable } from 'mobx-react';
import { toJS } from 'mobx';
import { parseInt, range } from 'lodash-es';
import {
  Alert,
  Col,
  ListGroup,
  ListGroupItem,
  ListGroupItemHeading,
  ListGroupItemText,
  Modal,
  ModalBody,
  Row,
  Button
} from 'reactstrap';
import { faChevronRight, faTimes, faUser, IconDefinition } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ApiRoomContainer } from '@ibe/api';
import { useTheme } from '@emotion/react';
import { LoggerFactory } from '@ibe/services';
import { useDeepCompareEffect, useMount } from 'react-use';
import IsoDropdown from '../Dropdown/Dropdown';
import PaxRoomSelectionController from './PaxRoomSelectionController';
import useTranslation from '../../../Hooks/useTranslation';
import fallback from '../../../../_Translations/generated/pax-room-selection.en.t.json';
import Keys from '../../../../_Translations/generated/pax-room-selection.en.t.json.keys';
import { Select } from '../../Form/Select/Select';
import GroupRequestModule from '../../GroupRequest/GroupRequestModule';
import useQuery from '../../../../MMB/Hooks/useQuery';
import useConfig from '../../../Hooks/useConfig';
import useWindow from '../../../Hooks/useWindow';
import PlusMinus from '../../PlusMinus/PlusMinus';
import ChildAgePicker from './ChildAgePicker';

export type PaxRoomSelectionImperativeHandleRefType = Ref<{
  getTitle?: () => string | ReactNode;
  toggleDropdown?: () => void;
  toggleDropdownOnly?: (a: boolean) => void;
  getCurrentPax?: () => ApiRoomContainer[];
  validateChildrenSelection?: () => boolean;
}>;

export interface Props {
  initialOccupancy?: ApiRoomContainer[]; // TODO Partial<> is nicer for the consumer
  value?: ApiRoomContainer[]; // TODO Partial<> is nicer for the consumer
  minAdults?: number; // default 1
  maxAdults?: number; // default 10
  maxChildren?: number; // default 10
  maxChildAge?: number; // default 17
  maxCapacity?: number; // default 9
  maxRooms?: number;
  childAgeRestriction?: number;
  onChange: (o: ApiRoomContainer[]) => void;
  additionalElementsBefore?: Array<JSX.Element>;
  additionalElementsAfter?: Array<JSX.Element>;
  withChildAgePicker?: boolean | false;
  withRoomCountSelector?: boolean; // default true
  withGroupRequestForm?: boolean;
  paxTitleKey?: string;
  customTitle?: string | ReactNode;
  participantsCountInTitle?: boolean;
  id?: string;
  groupRequestWithNoAuthorization?: boolean;
  childAgePlaceholder?: string;
  minSelectableChildAge?: number;
  withChildrenSelection?: boolean | true;
  withInfantSelection?: boolean | false;
  withAddRoomButton?: boolean | false;
  withAdditionalLabel?: boolean | false;
  maxCapacityPerRoom?: number;
  onlyAdultSelect?: boolean;
  onSet?: () => void;
  searchNoDefaultPax?: boolean;
  searchPaxInputSearchable?: boolean;
  searchPaxAutoFocus?: boolean;
  staticLabel?: string;
  showLabelOptionsWithDesc?: boolean;
  disabled?: boolean;
  dropdownIcon?: IconDefinition;
  imperativeHandleRef?: PaxRoomSelectionImperativeHandleRefType;
  rightAlignedMenu?: boolean;
  onDropdownOpenChange?: (dropdownOpen: boolean) => void;
  plusMinusMode?: boolean;
  inputStepper?: boolean;
  fillWithValueOnOpen?: boolean;
  useInitialPersonAmounts?: boolean;
  displayInAnotherLayer?: boolean;
  validationErrorComponent?: (errorKeys?: string[]) => ReactNode;
  validateChildrenPaxSelectionOnLeave?: boolean;
  validateChildrenPaxSelectionOnChange?: boolean;
  revalidateChildrenOnChangeAfterError?: boolean;
  updatePaxOnLeaveWhenValid?: boolean;
  updatePaxOnChangeWhenValid?: boolean;
  skipSearch?: boolean;
  tabIndex?: number | null;
  accessibilityModal?: boolean;
  childAgePickerHeader?: string;
  useCustomDropdownToggle?: boolean;
}

export const PaxRoomSelection = observer(
  forwardRef(function PaxRoomSelection(props: Props, ref: Ref<HTMLDivElement>): JSX.Element {
    const window = useWindow();
    const logger = LoggerFactory.get('PaxRoomSelection');
    const { t } = useTranslation('pax-room-selection', fallback, Keys);
    const onChange = props.onChange || ((p): void => logger.log('PaxRoomSelection.onChange', p));
    const state = useLocalObservable(() => new PaxRoomSelectionController());
    const stateRooms = state.rooms;
    const theme = useTheme();
    const config = useConfig();
    const hasOccupancyParam = !!useQuery().param('occupancy');

    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [groupRequestFormIsOpen, setGroupRequestFormIsOpen] = useState<boolean>(false);
    const toggleButtonClicked = useRef<boolean>(false);

    const [initialPaxSelectionOverwritten, setInitialPaxSelectionOverwritten] = useState(false);

    const [windowSize, setWindowSize] = useState(getWindowSize());

    state.setMinAdults(props.minAdults || config.paxRoomConfig.minAdults || 1);
    state.setMaxAdults(
      (props.maxAdults || config.paxRoomConfig.maxAdults || state.defaultPersonsAmount) + 1
    );
    state.setMaxChildren(
      (props.maxChildren || config.paxRoomConfig.maxChildren || state.defaultPersonsAmount) + 1
    );
    state.setMaxCapacity(props.maxCapacity || config.paxRoomConfig.maxCapacity);
    state.setMaxCapacityPerRoom(props.maxCapacityPerRoom);
    state.setMaxRooms(props.maxRooms || config.paxRoomConfig.maxRooms);

    const { withChildAgePicker, childAgeRestriction } = props;
    const withRoomCountSelector = props.withRoomCountSelector !== false;

    const [validAges, setValidAges] = useState(true);
    const [areChildrenOverXYears, setAreChildrenOverXYears] = useState(true);
    const [errorKeys, setErrorKeys] = useState<string[]>([]);
    const initialRender = useRef<boolean>(false);
    const {
      customTitle,
      participantsCountInTitle,
      childAgePlaceholder,
      paxTitleKey,
      additionalElementsAfter,
      initialOccupancy,
      value,
      withGroupRequestForm = true,
      groupRequestWithNoAuthorization,
      minSelectableChildAge,
      withChildrenSelection = true,
      withInfantSelection = false,
      withAddRoomButton = false,
      withAdditionalLabel = false,
      onlyAdultSelect,
      onSet,
      searchNoDefaultPax,
      searchPaxInputSearchable = false,
      searchPaxAutoFocus,
      staticLabel,
      showLabelOptionsWithDesc,
      disabled,
      dropdownIcon,
      imperativeHandleRef,
      rightAlignedMenu,
      onDropdownOpenChange,
      plusMinusMode,
      inputStepper,
      fillWithValueOnOpen,
      useInitialPersonAmounts,
      displayInAnotherLayer,
      validationErrorComponent,
      validateChildrenPaxSelectionOnLeave,
      revalidateChildrenOnChangeAfterError,
      updatePaxOnLeaveWhenValid,
      validateChildrenPaxSelectionOnChange,
      updatePaxOnChangeWhenValid,
      skipSearch,
      accessibilityModal,
      childAgePickerHeader,
      useCustomDropdownToggle
    } = props;

    useMount(() => {
      initialRender.current = true;
    });

    useImperativeHandle(
      imperativeHandleRef,
      () => ({
        getTitle: () => paxTitle(),
        toggleDropdown,
        toggleDropdownOnly: (a: boolean) => setDropdownOpen(a),
        getCurrentPax: () => toJS(state.rooms),
        validateChildrenSelection
      }),
      [dropdownOpen, withChildAgePicker, childAgeRestriction, stateRooms, value]
    );

    const accessibilityModalRef = useRef<HTMLDivElement>(null);
    const [accessibilityModalOpen, setAccessibilityModalOpen] = useState(false);
    const icon = !!dropdownIcon
      ? dropdownIcon
      : theme.paxSelectionOptions && theme.paxSelectionOptions?.withIcon
      ? faUser
      : undefined;

    function toggleGroupRequestForm(newValue: boolean): void {
      setGroupRequestFormIsOpen(newValue);
    }

    function withButton(): boolean {
      if (skipSearch) {
        return false;
      }
      if (config.components.paxRoomSelection) {
        if (windowSize.innerWidth > 768) {
          return !config.components.paxRoomSelection.disableButton;
        }
      }
      return true;
    }

    useDeepCompareEffect((): void => {
      if (initialPaxSelectionOverwritten) {
        return;
      }
      if (!!value && value.length > 0) {
        state.setRoomCount(value.length);
        state.setRooms([...value]);
        state.setInitialOccupancy({ ...value[0] });
      } else if (!!initialOccupancy && initialOccupancy.length > 0) {
        state.setRoomCount(initialOccupancy.length);
        state.setRooms([...initialOccupancy]);
        state.setInitialOccupancy(initialOccupancy[0]);
      } else {
        state.setRoomCount(1);
        if (searchNoDefaultPax) {
          state.setRooms([
            { adults: 0, children: 0, infants: 0, childrenAges: [], infantsAges: [] }
          ]);
        }
        state.triggerRoomCreation();
      }
    }, [value, state, initialOccupancy]);

    useEffect(() => {
      if (validateChildrenPaxSelectionOnLeave && !dropdownOpen) {
        const paxIsValid = validateChildrenSelection();
        if (updatePaxOnLeaveWhenValid && paxIsValid) {
          onChange(toJS(state.rooms));
        }
      }
    }, [validateChildrenPaxSelectionOnLeave, dropdownOpen]);

    const validateChildrenPaxSelectionOnChangeFunc = () => {
      if (validateChildrenPaxSelectionOnChange) {
        const paxIsValid = validateChildrenSelection();
        if (paxIsValid && updatePaxOnChangeWhenValid) {
          onChange(toJS(state.rooms));
        }
      }
    };

    useEffect(() => {
      if (validationErrorComponent) {
        const allErrors: string[] = [];
        if (!validAges) {
          allErrors.push(Keys.validationErrorMessage);
        }
        if (!areChildrenOverXYears) {
          allErrors.push(Keys.childAgeErrorMessage);
        }
        setErrorKeys([...allErrors]);
      }
    }, [validAges, areChildrenOverXYears]);

    useEffect(() => {
      if (
        dropdownOpen &&
        initialRender.current &&
        !!value &&
        value.length > 0 &&
        fillWithValueOnOpen
      ) {
        state.setRoomCount(value.length);
        state.setRooms([...value]);
      }
    }, [fillWithValueOnOpen, dropdownOpen, initialRender]);

    useEffect(() => {
      function handleWindowResize() {
        setWindowSize(getWindowSize());
      }

      window?.addEventListener('resize', handleWindowResize);

      return () => {
        window?.removeEventListener('resize', handleWindowResize);
      };
    }, []);

    function getWindowSize() {
      const { innerWidth = 0, innerHeight = 0 } = window || {};
      return { innerWidth, innerHeight };
    }

    const validateChildrenSelection = (): boolean => {
      if (withChildAgePicker) {
        const agesAreValid = state.validateProvidedAges();
        setValidAges(agesAreValid);
        if (!agesAreValid) {
          return false;
        }
        if (childAgeRestriction) {
          const areChildrenOver2Years = state.areChildrenOverXYears(childAgeRestriction);
          setAreChildrenOverXYears(areChildrenOver2Years);
          if (!areChildrenOver2Years) {
            return false;
          }
        } else {
          return true;
        }
      } else {
        return true;
      }
      return true;
    };

    const onClick = (): void => {
      if (!validateChildrenSelection()) {
        return;
      }

      setDropdownOpen(false);
      if (!!onDropdownOpenChange) {
        onDropdownOpenChange(false);
      }
      onChange(toJS(state.rooms));
      if (!initialPaxSelectionOverwritten) {
        setInitialPaxSelectionOverwritten(true);
      }
      if (onSet) {
        onSet();
      }
      if (displayInAnotherLayer) {
        toggleDropdown();
      }
      if (accessibilityModal) {
        setAccessibilityModalOpen(false);
      }
    };

    const buildPaxTitle = (): string => {
      if (theme.paxSelectionOptions && theme.paxSelectionOptions?.shortTitle) {
        return state.travelersAmount + (withRoomCountSelector ? `, ${state.roomAmount}` : '');
      }
      if (theme.paxSelectionOptions?.longTitle) {
        let roomTitle = '';
        if (withRoomCountSelector) {
          roomTitle = state.roomAmount + ' ' + t(Keys.room, { count: state.roomAmount }) + ', ';
        }

        return `${roomTitle}${state.numberOfAdults} ${t(Keys.adult, {
          count: state.numberOfAdults
        })}${
          state.numberOfChildren > 0
            ? `, ${state.numberOfChildren} ${t(Keys.child, { count: state.numberOfChildren })}`
            : ''
        }`;
      }
      return `${t(paxTitleKey || Keys.traveler, { count: state.travelersAmount })}: ${
        state.travelersAmount
      }${
        withRoomCountSelector
          ? `  -  ${t(Keys.room, { count: state.roomAmount })}: ${state.roomAmount}`
          : ''
      }`;
    };

    const maxRooms = props.maxRooms || config?.paxRoomConfig?.maxRooms;

    const buildPaxSelectionContent = (): JSX.Element => {
      return (
        <div className={'dropdown__wrapper'}>
          <ListGroup flush className={plusMinusMode ? 'paxRoomSelection__plus-minus' : ''}>
            {withRoomCountSelector && (
              <>
                <ListGroupItem
                  className={`paxRoomSelection__rooms ${(!maxRooms || maxRooms === 1) && 'd-none'}`}
                >
                  {props.additionalElementsBefore && <>{props.additionalElementsBefore}</>}
                  {plusMinusMode ? (
                    <>
                      {!!maxRooms && maxRooms > 1 && !withAddRoomButton && (
                        <Row>
                          <Col xs={12} sm={6}>
                            <PlusMinus
                              label={t(Keys.room, { count: 0 })}
                              currentCount={parseInt(state.roomCount?.value || '0', 10)}
                              onCountChange={(countValue: number): void =>
                                state.setRoomCountAndTriggerRoomCreation({
                                  value: countValue.toString(),
                                  label: countValue.toString()
                                })
                              }
                              minCount={1}
                              maxCount={state.maxRooms - 1}
                            />
                          </Col>
                        </Row>
                      )}
                    </>
                  ) : (
                    <>
                      <ListGroupItemHeading className="paxRoomSelection__heading">
                        {t(Keys.room, { count: 0 })}
                      </ListGroupItemHeading>
                      <Select
                        options={state.roomCountOptions}
                        selectedOption={state.roomCount}
                        onChange={(val): void => {
                          state.setRoomCountAndTriggerRoomCreation(val);
                          validateChildrenPaxSelectionOnChangeFunc();
                        }}
                        getLabel={(option): string => (option ? option.label : '')}
                        getValue={(option): string => (option ? option.value : '')}
                      />
                    </>
                  )}
                </ListGroupItem>
              </>
            )}
            {state.rooms &&
              state.rooms.length > 0 &&
              state.rooms.map((room: ApiRoomContainer, i: number) => (
                <li key={i} className="paxRoomSelection__item ">
                  <ul>
                    <ListGroupItem>
                      {!withRoomCountSelector && props.additionalElementsBefore && (
                        <>{props.additionalElementsBefore}</>
                      )}
                      <Row>
                        <Col>
                          {withRoomCountSelector &&
                            (!plusMinusMode || (plusMinusMode && !!maxRooms && maxRooms > 1)) && (
                              <>
                                {withAddRoomButton ? (
                                  <div className="paxRoomSelection__extended-heading">
                                    <p className="paxRoomSelection__heading">
                                      {t(Keys.room)} {i + 1}
                                    </p>

                                    {withAddRoomButton && i > 0 && (
                                      <p>
                                        <button
                                          className="paxRoomSelection__remove-room"
                                          onClick={() => state.triggerRemoveRoom(i)}
                                        >
                                          X
                                        </button>
                                      </p>
                                    )}
                                  </div>
                                ) : (
                                  <p className="paxRoomSelection__heading">
                                    {t(Keys.room)} {i + 1}
                                  </p>
                                )}
                              </>
                            )}
                          <Row>
                            {plusMinusMode ? (
                              <Col
                                xs={12}
                                sm={withAddRoomButton ? 12 : 6}
                                className={'paxRoomSelection__item__person'}
                              >
                                <PlusMinus
                                  label={
                                    withAdditionalLabel
                                      ? `${t(Keys.adult, { count: 2 })}`
                                      : `${t(Keys.adult, { count: 2 })} ${t(Keys.adultAgeRange)}`
                                  }
                                  additionalLabel={
                                    withAdditionalLabel ? t(Keys.adultAgeRange) : undefined
                                  }
                                  inputStepper={inputStepper}
                                  currentCount={parseInt(
                                    state.getPersonCountOption(room.adults)?.value || '0',
                                    10
                                  )}
                                  onCountChange={(countValue: number): void =>
                                    state.setAdultForRoom(i, {
                                      value: countValue.toString(),
                                      label: countValue.toString()
                                    })
                                  }
                                  minCount={state.getMinAdults()}
                                  maxCount={
                                    props.maxCapacityPerRoom
                                      ? state.getMaxAdultsLeftPerRoom(i)
                                      : (useInitialPersonAmounts
                                          ? state.initialMaxAdults
                                          : state.getMaxAdults(i)) - 1
                                  }
                                  disabled={disabled}
                                  validationFailed={
                                    state.getAdultCountInRoom(i) >= state.getMaxAdultsLeftPerRoom(i)
                                  }
                                  occupancyErrorMessage={t(Keys.occupancyErrorMessage)}
                                  plusButtonId={`plus-button-adult-${i}`}
                                />
                              </Col>
                            ) : (
                              <Col>
                                <ListGroupItemText className="text-nowrap">
                                  {t(Keys.adult, { count: 2 })} {t(Keys.adultAgeRange)}
                                </ListGroupItemText>
                                <Select
                                  options={range(state.getMinAdults(), state.getMaxAdults(i)).map(
                                    val => {
                                      return state.getPersonCountOption(val);
                                    }
                                  )}
                                  selectedOption={state.getPersonCountOption(room.adults)}
                                  onChange={(val): void => {
                                    state.setAdultForRoom(i, val);
                                    validateChildrenPaxSelectionOnChangeFunc();
                                  }}
                                  getLabel={(option): string => (option ? option.label : '')}
                                  getValue={(option): string => (option ? option.value : '')}
                                  isSearchable={searchPaxInputSearchable}
                                  id={'paxRoomSelection__select_input_adult' + i}
                                  autoFocus={searchPaxAutoFocus}
                                  isDisabled={disabled}
                                />
                              </Col>
                            )}
                            {withChildrenSelection &&
                              (plusMinusMode ? (
                                <Col
                                  xs={12}
                                  sm={withAddRoomButton ? 12 : 6}
                                  className="mt-3 mt-sm-0 paxRoomSelection__item__person"
                                >
                                  <PlusMinus
                                    label={
                                      withAdditionalLabel
                                        ? `${t(Keys.child, { count: 2 })}`
                                        : `${t(Keys.child, { count: 2 })} ${t(Keys.childAgeRange)}`
                                    }
                                    additionalLabel={
                                      withAdditionalLabel ? t(Keys.childAgeRange) : undefined
                                    }
                                    inputStepper={inputStepper}
                                    currentCount={parseInt(
                                      state.getPersonCountOption(room.children)?.value || '0',
                                      10
                                    )}
                                    onCountChange={(countValue: number): void =>
                                      state.setChildrenForRoom(i, {
                                        value: countValue.toString(),
                                        label: countValue.toString()
                                      })
                                    }
                                    minCount={0}
                                    maxCount={
                                      props.maxCapacityPerRoom
                                        ? state.getMaxChildrenLeftPerRoom(i)
                                        : (useInitialPersonAmounts
                                            ? state.initialMaxChildren
                                            : state.getMaxChildren(i)) - 1
                                    }
                                    disabled={disabled}
                                    plusButtonId={`plus-button-child-${i}`}
                                    validationFailed={
                                      state.getChildrenCountInRoom(i) >=
                                      state.getMaxChildrenLeftPerRoom(i)
                                    }
                                    occupancyErrorMessage={t(Keys.occupancyErrorMessage)}
                                  />
                                </Col>
                              ) : (
                                <Col>
                                  <ListGroupItemText className="text-nowrap">
                                    {t(Keys.child, { count: 2 })} {t(Keys.childAgeRange)}
                                  </ListGroupItemText>
                                  <Select
                                    options={range(0, state.getMaxChildren(i)).map(val => {
                                      return state.getPersonCountOption(val);
                                    })}
                                    selectedOption={state.getPersonCountOption(room.children)}
                                    onChange={(val): void => {
                                      state.setChildrenForRoom(i, val);
                                      validateChildrenPaxSelectionOnChangeFunc();
                                    }}
                                    getLabel={(option): string => (option ? option.label : '')}
                                    getValue={(option): string => (option ? option.value : '')}
                                    isSearchable={searchPaxInputSearchable}
                                    autoFocus={searchPaxAutoFocus}
                                    isDisabled={disabled}
                                    id={'paxRoomSelection__select_input_child' + i}
                                  />
                                </Col>
                              ))}
                            {withInfantSelection &&
                              (plusMinusMode ? (
                                <Col
                                  xs={12}
                                  sm={withAddRoomButton ? 12 : 6}
                                  className={'paxRoomSelection__item__person'}
                                >
                                  <PlusMinus
                                    label={
                                      withAdditionalLabel
                                        ? `${t(Keys.infant, { count: 2 })}`
                                        : `${t(Keys.infant, { count: 2 })} ${t(
                                            Keys.infantAgeRange
                                          )}`
                                    }
                                    additionalLabel={
                                      withAdditionalLabel ? t(Keys.infantAgeRange) : undefined
                                    }
                                    inputStepper={inputStepper}
                                    currentCount={parseInt(
                                      state.getPersonCountOption(room.infants)?.value || '0',
                                      10
                                    )}
                                    onCountChange={(countValue: number): void =>
                                      state.setInfantForRoom(i, {
                                        value: countValue.toString(),
                                        label: countValue.toString()
                                      })
                                    }
                                    minCount={0}
                                    maxCount={
                                      props.maxCapacityPerRoom
                                        ? state.getMaxInfantsLeftPerRoom(i)
                                        : 2
                                    }
                                    disabled={disabled}
                                    plusButtonId={`plus-button-infant-${i}`}
                                    validationFailed={
                                      state.getInfantCountInRoom(i) >=
                                      state.getMaxInfantsLeftPerRoom(i)
                                    }
                                    occupancyErrorMessage={t(Keys.occupancyErrorMessage)}
                                  />
                                </Col>
                              ) : (
                                <Col>
                                  <ListGroupItemText className="text-nowrap">
                                    {t(Keys.child, { count: 2 })} {t(Keys.childAgeRange)}
                                  </ListGroupItemText>
                                  <Select
                                    options={range(0, state.getMaxChildren(i)).map(val => {
                                      return state.getPersonCountOption(val);
                                    })}
                                    selectedOption={state.getPersonCountOption(room.children)}
                                    onChange={(val): void => {
                                      state.setChildrenForRoom(i, val);
                                      validateChildrenPaxSelectionOnChangeFunc();
                                    }}
                                    getLabel={(option): string => (option ? option.label : '')}
                                    getValue={(option): string => (option ? option.value : '')}
                                    isSearchable={searchPaxInputSearchable}
                                    autoFocus={searchPaxAutoFocus}
                                    isDisabled={disabled}
                                    id={'paxRoomSelection__select_input_child' + i}
                                  />
                                </Col>
                              ))}
                          </Row>
                        </Col>
                      </Row>
                    </ListGroupItem>
                  </ul>
                  {childAgePickerHeader && room.children > 0 ? (
                    <div className="paxRoomSelection__child__heading">{childAgePickerHeader}</div>
                  ) : null}
                  {withChildAgePicker && (
                    <>
                      {plusMinusMode ? (
                        <ListGroupItem className="paxRoomSelection__child-age-picker__container">
                          <Row className="paxRoomSelection__child-age-picker">
                            {[...Array(room.children).keys()].map(key => (
                              <Col xs={12} sm={6} md={room.children <= 2 ? 6 : 3} className="mt-2">
                                <ChildAgePicker
                                  key={key}
                                  roomIndex={i}
                                  childIndex={key}
                                  maxChildAge={props.maxChildAge}
                                  state={state}
                                  childAgePlaceholder={childAgePlaceholder}
                                  minSelectableChildAge={minSelectableChildAge}
                                  disabled={disabled}
                                  plusMinusMode={plusMinusMode}
                                  searchPaxAutoFocus={searchPaxAutoFocus}
                                  searchPaxInputSearchable={searchPaxInputSearchable}
                                  revalidateChildrenOnChangeAfterError={
                                    revalidateChildrenOnChangeAfterError
                                  }
                                  validateChildrenSelection={validateChildrenSelection}
                                  paxHasError={!validAges || !areChildrenOverXYears}
                                />
                              </Col>
                            ))}
                          </Row>
                        </ListGroupItem>
                      ) : (
                        [...Array(room.children).keys()].map(key => (
                          <ChildAgePicker
                            key={key}
                            roomIndex={i}
                            childIndex={key}
                            maxChildAge={props.maxChildAge}
                            state={state}
                            childAgePlaceholder={childAgePlaceholder}
                            minSelectableChildAge={minSelectableChildAge}
                            disabled={disabled}
                            plusMinusMode={plusMinusMode}
                            searchPaxAutoFocus={searchPaxAutoFocus}
                            searchPaxInputSearchable={searchPaxInputSearchable}
                            revalidateChildrenOnChangeAfterError={
                              revalidateChildrenOnChangeAfterError
                            }
                            validateChildrenSelection={validateChildrenSelection}
                            paxHasError={!validAges || !areChildrenOverXYears}
                          />
                        ))
                      )}
                    </>
                  )}
                  {additionalElementsAfter && (
                    <>
                      <ListGroupItem>{additionalElementsAfter}</ListGroupItem>
                    </>
                  )}
                </li>
              ))}
            {withGroupRequestForm && (
              <ListGroupItem>
                <GroupRequestModule
                  isOpen={groupRequestFormIsOpen}
                  setIsOpen={toggleGroupRequestForm}
                  withNoAuthorization={groupRequestWithNoAuthorization}
                />
              </ListGroupItem>
            )}
            <li className="paxRoomSelection__spacing__when__mobile"></li>
            <ListGroupItem className="paxRoomSelection__bottom">
              {!validAges && !validationErrorComponent && (
                <Alert color="danger">{t(Keys.validationErrorMessage)}</Alert>
              )}
              {!areChildrenOverXYears && !validationErrorComponent && (
                <Alert color="danger">{t(Keys.childAgeErrorMessage)}</Alert>
              )}
              {validationErrorComponent ? validationErrorComponent(errorKeys) : null}
              {withAddRoomButton && (
                <>
                  <hr className="border-t-3 border-darkgrey my-4" />
                  {state.allTravelersAmount < state.getMaxCapacity && (
                    <button
                      className="paxRoomSelection__add-room"
                      onClick={() => state.createRoom()}
                    >
                      {t(Keys.addRoom)}
                    </button>
                  )}
                </>
              )}
              {withButton() && (
                <Button
                  tabIndex={0}
                  color={plusMinusMode ? 'primary' : 'secondary'}
                  block
                  onClick={onClick}
                >
                  {plusMinusMode ? (
                    <>
                      <span>{t(Keys.updateSearch)}</span>
                      <FontAwesomeIcon
                        icon={faChevronRight}
                        className="ml-2 paxRoomSelection__confirm-button-icon"
                      />
                    </>
                  ) : (
                    t(Keys.button)
                  )}
                </Button>
              )}
            </ListGroupItem>
          </ListGroup>
        </div>
      );
    };

    const paxTitle = (): string | ReactNode => {
      if (customTitle && participantsCountInTitle) {
        return (
          <div className={'text-left'}>
            <div>{customTitle}</div>
            <div>{state.allTravelersAmount}</div>
          </div>
        );
      }
      if (
        searchNoDefaultPax &&
        state.rooms.every(room => !room.adults && !room.children && !room.infants)
      ) {
        return t(Keys.pleaseSelect);
      } else if (theme.paxSelectionOptions?.displayStaticTitleOnlyBeforeSelection) {
        if (hasOccupancyParam) {
          return buildPaxTitle();
        } else if (customTitle && !initialPaxSelectionOverwritten) {
          return customTitle;
        }
      } else if (customTitle) {
        return customTitle;
      }

      return buildPaxTitle();
    };

    const toggleDropdown = (): void => {
      if ((!withButton() || plusMinusMode) && dropdownOpen) {
        if (
          !plusMinusMode ||
          (plusMinusMode && toggleButtonClicked.current) ||
          (plusMinusMode && skipSearch)
        ) {
          toggleButtonClicked.current = false;
          onClick();
        }
      }
      if (!groupRequestFormIsOpen) {
        setDropdownOpen(!dropdownOpen);
        if (!!onDropdownOpenChange) {
          onDropdownOpenChange(!dropdownOpen);
        }
      }
    };

    const onToggleButtonClicked = (): void => {
      if (dropdownOpen) {
        toggleButtonClicked.current = true;
      }
    };

    return onlyAdultSelect ? (
      <>
        {state.rooms &&
          state.rooms.length > 0 &&
          state.rooms.map((room: ApiRoomContainer, i: number) => (
            <span className="paxRoomSelection" key={JSON.stringify(room)}>
              <ListGroupItem>
                <Row>
                  <Col>
                    {!!staticLabel ? (
                      <span className="paxRoomSelection__label">{staticLabel}</span>
                    ) : (
                      <></>
                    )}
                    <Select
                      options={range(state.getMinAdults(), state.getMaxAdults(i)).map(val => {
                        return state.getPersonCountOption(val);
                      })}
                      selectedOption={state.getPersonCountOption(room.adults)}
                      onChange={(val): void => {
                        state.setAdultForRoom(i, val, onClick);
                        validateChildrenPaxSelectionOnChangeFunc();
                      }}
                      getLabel={(option): string =>
                        option
                          ? showLabelOptionsWithDesc
                            ? option.label
                                .concat(' ')
                                .concat(t(Keys.adult, { count: Number(option.value) }))
                            : option.label
                          : ''
                      }
                      getValue={(option): string => (option ? option.value : '')}
                      isDisabled={disabled}
                      isSearchable={searchPaxInputSearchable}
                    />
                  </Col>
                </Row>
              </ListGroupItem>
              {additionalElementsAfter && (
                <>
                  <ListGroupItem>{additionalElementsAfter}</ListGroupItem>
                </>
              )}
            </span>
          ))}
      </>
    ) : (
      <div
        className={`paxRoomSelection${
          !!state.rooms && state.rooms.length > 0 ? ' input--filled' : ''
        }`}
        ref={ref}
      >
        {displayInAnotherLayer ? (
          <div className="pax-room-selection__modal">
            <Button
              outline
              className={'w-100 text-truncate pax-room-selection__button p-1 text-center'}
              onClick={toggleDropdown}
            >
              {paxTitle() || ''}
            </Button>
            <Modal
              className="pax-room-selection__modal"
              isOpen={dropdownOpen}
              toggle={toggleDropdown}
              container={
                config.outsideElementsContainerId
                  ? window?.document?.getElementById(config.outsideElementsContainerId) || undefined
                  : undefined
              }
            >
              <ModalBody>
                <Row className="justify-content-between align-items-center mb-2 px-2">
                  <span className="pax-room-selection__modal__label">
                    {t(Keys.traveler, { count: 2 })}
                  </span>
                  <button aria-description="close" onClick={toggleDropdown}>
                    <FontAwesomeIcon icon={faTimes} className="pax-room-selection__modal__close" />
                  </button>
                </Row>
                {buildPaxSelectionContent()}
              </ModalBody>
            </Modal>
          </div>
        ) : (
          <>
            {accessibilityModal && (
              <div className="pax-room-selection__modal w-100" ref={accessibilityModalRef}>
                <Modal
                  className="pax-room-selection__modal overflow-auto"
                  isOpen={accessibilityModalOpen}
                  toggle={() => setAccessibilityModalOpen(p => !p)}
                  container={accessibilityModalRef as RefObject<HTMLElement>}
                  unmountOnClose
                >
                  <ModalBody>{buildPaxSelectionContent()}</ModalBody>
                </Modal>
                <Button
                  outline
                  className={'pax-room-selection__button sr-only sr-only-focusable'}
                  onClick={() => setAccessibilityModalOpen(p => !p)}
                  disabled={disabled}
                >
                  <div className="d-flex">
                    {icon && (
                      <div className="ml-auto">
                        <FontAwesomeIcon icon={icon} />
                      </div>
                    )}
                    <div className="w-100 text-truncate">{paxTitle() || ''}</div>
                  </div>
                </Button>
              </div>
            )}
            <IsoDropdown
              isOpen={dropdownOpen}
              onDropdownToggle={toggleDropdown}
              icon={icon}
              title={paxTitle()}
              id={props.id}
              disabled={disabled}
              right={rightAlignedMenu}
              onToggleButtonClick={onToggleButtonClicked}
              tabIndex={props.tabIndex}
              closeCustomDropdownToggle={
                useCustomDropdownToggle
                  ? () => {
                      setDropdownOpen(false);
                      if (onDropdownOpenChange) {
                        onDropdownOpenChange(false);
                      }
                    }
                  : undefined
              }
            >
              {buildPaxSelectionContent()}
            </IsoDropdown>
          </>
        )}
      </div>
    );
  })
);
export default PaxRoomSelection;
